/**
 * 72.3 step 3 (ARCHITECTURE §5.35 "A collapse in time, and what it changes"; DECISIONS 323: load path + capacity, a falling floor
 * can kill): the support solve acting. Behind GameConfig.collapse (with modular buildings); off, nothing here runs and no field is
 * written.
 *
 * - **A collapse starts** on the tick a modular building's support solve (core/moduleSupport.ts, memoised on its module healths)
 *   says something would fail: its first pass COLLAPSE.passTicks later (`StructureState.collapse`).
 * - **A pass** solves once over the building as it stands, with what has landed on each slab × COLLAPSE.impact added to its load.
 *   Every module that fails goes at once (its health 0: its shape leaves the walk, sight and the bullets) and falls straight down
 *   its column (`landingOf`) to the highest standing plate under it, or the ground, landing COLLAPSE.fallTicks[storeys] later.
 *   The next pass comes passTicks on while anything failed, else at the next landing; with nothing failing or falling, it ends.
 * - **A landing** lies as a heap where it came down (`StructureState.rubble` with its foot: RUBBLE_KINDS' heaps and the slabs'
 *   decks — today's heaps; the mounds are step 4's). On a plate its mass loads that plate's slabs for the passes after (a roof
 *   brings a floor down); on the ground it strikes whoever stands under it, COLLAPSE.blowPerWeight a load unit of its mass — from
 *   COLLAPSE.killWeight (a floor slab) it can kill, lighter it is never the last of anyone's health (141). A heap lying on a
 *   slab that falls goes with it (its weight in the slab's mass).
 * - **Step 6, its health is its modules'** (`healthFromModules`): its full health times the share of its weight standing; under
 *   COLLAPSE.ruinShare, destroyed (72.1's ruin). damageStructure leaves a building so marked alone.
 *
 * Deterministic by tick: integers in fixed orders (structures, then falls as filed, then modules by index); the blows' reach is
 * dot products in the building's frame. Pure.
 */
import type { GameState, PlayerState, StructureCollapse, StructureState, Vec3 } from '../types';
import type { BuildingLayout } from '../types/modules';
import { COLLAPSE, MODULE_CELL, MODULE_SHAPES, MODULE_SUPPORT, RUBBLE_KINDS } from '../constants/modules';
import { layoutFor, posesOf } from './modules';
import { landingOf, seatOf, structureSupport, supportPass } from './moduleSupport';
import { structureFrame } from './hits';
import { getSurfaceRadius } from './surface';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { applyDamage } from './damage';
import { structureFullHealth } from './body';
import { vecAdd, vecDot, vecNormalize, vecScale, vecSub } from '../math';

type Fall = StructureCollapse['falls'][number];
type Heap = NonNullable<StructureState['rubble']>[number];

/** The match's clock: the action tick (the turn number on the turn clock). */
const tickOf = (state: Pick<GameState, 'tick' | 'turnNumber'>): number => state.tick ?? state.turnNumber;

/** cos and sin of a quarter turn (exact). */
const COS = [1, 0, -1, 0] as const, SIN = [0, 1, 0, -1] as const;

/** A module's box in the building's frame (people): its parts' extent turned and moved to its pose. */
function boxOf(layout: BuildingLayout, k: number): { x0: number; x1: number; y0: number; y1: number; z0: number; z1: number } {
  const m = layout.modules[k], pose = posesOf(layout)[k], turn = m.turn ?? 0, c = COS[turn], s = SIN[turn];
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity, z0 = Infinity, z1 = -Infinity;
  for (const p of MODULE_SHAPES[m.kind]) {
    if (p.leaf) continue;
    const [x, y, z] = p.centre, hx = turn % 2 ? p.half[2] : p.half[0], hz = turn % 2 ? p.half[0] : p.half[2];
    const cx = pose.x + x * c + z * s, cz = pose.z - x * s + z * c;
    x0 = Math.min(x0, cx - hx); x1 = Math.max(x1, cx + hx); z0 = Math.min(z0, cz - hz); z1 = Math.max(z1, cz + hz);
    y0 = Math.min(y0, pose.y + y - p.half[1]); y1 = Math.max(y1, pose.y + y + p.half[1]);
  }
  if (x0 === Infinity) return { x0: pose.x, x1: pose.x, y0: pose.y, y1: pose.y, z0: pose.z, z1: pose.z };
  return { x0, x1, y0, y1, z0, z1 };
}

/** A module's foot (people over the building's base): where its lowest part stands — the drawn fall runs from it to its landing. */
export function footOf(layout: BuildingLayout, k: number): number { return boxOf(layout, k).y0; }

/** Whether a module of this kind lies as a heap where it lands (the wall-like kinds' heaps, the slabs' decks). */
const heapsWhenFallen = (layout: BuildingLayout, k: number): boolean => RUBBLE_KINDS.has(layout.modules[k].kind) || COLLAPSE.heapKinds.has(layout.modules[k].kind);

/** `total` spread over `to` (the remainder one each to the first) into the landed list (ascending by module). */
function addLanded(landed: Array<[number, number]>, to: readonly number[], total: number): void {
  const q = Math.floor(total / to.length), r = total - q * to.length;
  to.forEach((k, i) => {
    const add = q + (i < r ? 1 : 0);
    if (add <= 0) return;
    const at = landed.findIndex(e => e[0] >= k);
    if (at >= 0 && landed[at][0] === k) landed[at] = [k, landed[at][1] + add];
    else landed.splice(at < 0 ? landed.length : at, 0, [k, add]);
  });
}

/**
 * The bodies under a module landing on the ground: the living units on the structure's planet, not airborne, whose feet are
 * inside its footprint (COLLAPSE.reachPeople and their radius on) at about the height it lands at — each struck by its mass.
 */
function blowsUnder(state: GameState, s: StructureState, layout: BuildingLayout, f: Fall): GameState {
  const planet = state.planets[s.planetIndex];
  if (!planet) return state;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, up);
  const base = vecAdd(planet.position, vecScale(up, getSurfaceRadius(planet, up) + (s.baseLift ?? 0)));
  const b = boxOf(layout, f.k), reach = COLLAPSE.reachPeople + ps.unitRadius / person;
  const blow = f.mass * COLLAPSE.blowPerWeight, kills = f.mass >= COLLAPSE.killWeight;
  let out = state;
  for (const u of state.players as readonly PlayerState[]) {
    if (!u.alive || u.airborne || u.planetIndex !== s.planetIndex) continue;
    const un = vecNormalize(u.surfaceDir), r = vecSub(vecAdd(planet.position, vecScale(un, getSurfaceRadius(planet, un))), base);
    const px = vecDot(r, tx) / person, py = vecDot(r, up) / person, pz = vecDot(r, tz) / person;
    // (a body on the ground floor stands on its tiles, which sit on the plinth over the terrain: a storey under the landing up to a person over it)
    if (px < b.x0 - reach || px > b.x1 + reach || pz < b.z0 - reach || pz > b.z1 + reach || py < f.y - MODULE_CELL || py > f.y + 1) continue;
    const now = out.players.find(p => p.id === u.id);
    if (!now?.alive) continue;
    const amount = kills ? blow : Math.min(blow, now.health - 1);
    if (amount > 0) out = applyDamage(out, { unit: u.id }, amount, 'drop').state;
  }
  return out;
}

/** Storeys from a module's foot down to `y` (people), as fallTicks indexes them. */
const storeysTo = (layout: BuildingLayout, k: number, y: number): number => Math.min(COLLAPSE.fallTicks.length - 1, Math.max(0, Math.round((boxOf(layout, k).y0 - y) / MODULE_CELL)));

/** Fall `f` going on past the slabs it was to land on (they fell first): to where its column lands now, the rest of its fall's time on. */
function fallOn(layout: BuildingLayout, modules: readonly number[], f: Fall): Fall {
  const { onto } = landingOf(layout, modules.map(h => h > 0), f.k);
  const y = onto.length ? Math.max(...onto.map(o => boxOf(layout, o).y1)) : 0;
  const more = Math.max(1, COLLAPSE.fallTicks[storeysTo(layout, f.k, y)] - COLLAPSE.fallTicks[storeysTo(layout, f.k, f.y)]);
  return { ...f, lands: f.lands + more, y, onto };
}

const failsMemo = new WeakMap<readonly number[], boolean>();
const byDown = new WeakMap<BuildingLayout, Map<string, boolean>>();
/**
 * Whether the building as it stands would lose something (the solve's first pass fails a module). The solve reads only which
 * modules are down, so it is asked once per set of them: a round that wears a wall (a new health array, the same set) costs a key,
 * not a solve. Whole, it stands (every layout does: tests/moduleSupport.test.ts).
 */
function wouldFail(s: StructureState, layout: BuildingLayout): boolean {
  const mods = s.modules!, hit = failsMemo.get(mods);
  if (hit !== undefined) return hit;
  let key = '';
  for (let k = 0; k < mods.length; k++) if (!(mods[k] > 0)) key += `${k},`;
  let byKey = byDown.get(layout);
  if (!byKey) byDown.set(layout, (byKey = new Map()));
  let v = byKey.get(key);
  if (v === undefined) byKey.set(key, (v = key !== '' && structureSupport(s, layout).passes.length > 0));
  failsMemo.set(mods, v);
  return v;
}

/** The structure at `i` replaced. */
const withStructure = (state: GameState, i: number, s: StructureState): GameState => ({ ...state, structures: state.structures.map((x, j) => (j === i ? s : x)) });

/** A collapse's next event: its pass, or a landing before it. */
const nextEvent = (c: StructureCollapse): number => c.falls.reduce((t, f) => Math.min(t, f.lands), c.at);

/** One collapse tick of structure `i` (its landings due, then its pass when due). */
function collapseTick(state: GameState, i: number, layout: BuildingLayout, tick: number): GameState {
  const s = state.structures[i], c = s.collapse!;
  let out = state, rubble: Heap[] = s.rubble ? s.rubble.slice() : [];
  const landed: Array<[number, number]> = c.landed ? c.landed.map(e => [e[0], e[1]]) : [];
  // the landings due, in the order they were filed; one whose slabs fell before it came down falls on, from where it was
  let falls = c.falls.filter(f => f.lands > tick);
  for (const f of c.falls) {
    if (f.lands > tick) continue;
    if (f.onto.length && f.onto.every(o => !(s.modules![o] > 0))) { falls.push(fallOn(layout, s.modules!, f)); continue; }
    if (heapsWhenFallen(layout, f.k)) rubble.push({ k: f.k, at: tick, y: f.y, ...(f.onto.length ? { on: f.onto[0] } : {}) });
    if (f.onto.length) addLanded(landed, f.onto, f.mass);
    else out = blowsUnder(out, s, layout, f);
  }
  let modules = s.modules!, at = c.at;
  if (tick >= c.at) {
    const fails = supportPass(layout, modules, landed.map(([k, w]) => [k, w * COLLAPSE.impact] as const));
    if (fails.length) {
      modules = modules.slice();
      for (const k of fails) modules[k] = 0;
      const up = modules.map(h => h > 0);
      const gone = new Set(fails);
      for (const k of fails) {
        const { onto } = landingOf(layout, up, k);
        const y = onto.length ? Math.max(...onto.map(o => boxOf(layout, o).y1)) : 0;
        const lies = landed.find(e => e[0] === k)?.[1] ?? 0;
        falls.push({ k, from: tick, lands: tick + COLLAPSE.fallTicks[storeysTo(layout, k, y)], y, onto, mass: MODULE_SUPPORT[layout.modules[k].kind].weight + lies });
      }
      // what lay on a slab that fell goes with it (its weight in the slab's mass): its heap, and its landed load
      rubble = rubble.filter(h => !(h.on !== undefined ? gone.has(h.on) : h.y === undefined && (layout.modules[h.k].floor ?? 0) > 0 && seatOf(layout, h.k).length > 0 && seatOf(layout, h.k).every(x => !up[x])));
      for (let n = landed.length - 1; n >= 0; n--) if (gone.has(landed[n][0])) landed.splice(n, 1);
      at = tick + COLLAPSE.passTicks;
    } else at = falls.length ? falls.reduce((t, f) => Math.min(t, f.lands), Infinity) : -1;
  }
  const next: StructureState = { ...s, modules };
  if (rubble.length) next.rubble = rubble; else delete next.rubble;
  if (at < 0) delete next.collapse;
  else next.collapse = { at, falls, ...(landed.length ? { landed } : {}) };
  return withStructure(out, i, next);
}

/**
 * The collapses' tick: for each modular building, a collapse started when its support says something fails, and each collapse
 * under way stepped (its landings, its pass). The same state when nothing is due.
 */
export function stepCollapse(state: GameState): GameState {
  const tick = tickOf(state);
  let out = state;
  for (let i = 0; i < state.structures.length; i++) {
    out = collapseOne(out, i, tick);
    out = healthFromModules(out, i);   // (step 6: after its pass, so what a pass brings down counts the same tick)
  }
  return out;
}

/** One structure's collapse this tick: started, stepped, or ended with its ruin. */
function collapseOne(out: GameState, i: number, tick: number): GameState {
  const s = out.structures[i];
  if (!s.modules && !s.collapse) return out;
  if (s.health <= 0) {   // (the ruin is 72.1's: every module down, its heaps its own)
    if (s.collapse) { const { collapse: _c, ...rest } = s; out = withStructure(out, i, rest); }
    return out;
  }
  const layout = layoutFor(s, out.config);
  if (!layout || !s.modules) return out;
  if (!s.collapse) {
    if (!wouldFail(s, layout)) return out;
    return withStructure(out, i, { ...s, collapse: { at: tick + COLLAPSE.passTicks, falls: [] } });
  }
  if (tick < nextEvent(s.collapse)) return out;
  return collapseTick(out, i, layout, tick);
}

/** The structures with no layout of their own health (vehicles, the HQ, the pad …): never looked at again. */
const ownHealth = new WeakSet<StructureState>();
/** The share of a layout's weight standing (MODULE_SUPPORT's weights), per module-health array. */
const standingOf = new WeakMap<readonly number[], number>();
function standingShare(layout: BuildingLayout, modules: readonly number[]): number {
  let share = standingOf.get(modules);
  if (share === undefined) {
    let all = 0, up = 0;
    layout.modules.forEach((m, k) => { const w = MODULE_SUPPORT[m.kind].weight; all += w; if (modules[k] > 0) up += w; });
    standingOf.set(modules, (share = all > 0 ? up / all : 1));
  }
  return share;
}

/**
 * 72.3 step 6 (ARCHITECTURE §5.35; 323's lean, open question 5): a modular building's health is its modules' — its full health
 * times the share of its weight still standing, and 0 (72.1's ruin) under COLLAPSE.ruinShare once its collapse has settled (the
 * falls land first): one truth, not two. Marked
 * `healthFrom: 'modules'` the first tick it is seen, from which damageStructure leaves its own number alone (its modules take
 * the blows: blastModules, moduleBlow, the collapse). The motor pool's compound keeps its own (63.16 (7)).
 */
function healthFromModules(out: GameState, i: number): GameState {
  const s = out.structures[i];
  if (s.health <= 0 || ownHealth.has(s)) return out;
  const layout = s.type === 'motorPool' ? null : layoutFor(s, out.config);
  if (!layout) { ownHealth.add(s); return out; }
  const share = s.modules ? standingShare(layout, s.modules) : 1;
  // (under the ruin share it is destroyed once its collapse has settled: the falls play out and land, then the ruin)
  const health = share < COLLAPSE.ruinShare && !s.collapse ? 0 : structureFullHealth(out, s) * share;
  if (s.healthFrom === 'modules' && s.health === health) return out;
  return withStructure(out, i, { ...s, healthFrom: 'modules', health });
}
